import dayjs from 'dayjs';
import { Calendar } from 'primereact/calendar';
import type { KeyboardEvent } from 'react';

import { digitFitsDate, ISO_DATE } from '@shared/lib/date';

import { SHARED_COMPONENTS_TEXT } from '../constants';

export interface BaseDatePickerProps {
  id: string;
  /** ISO `YYYY-MM-DD` (what the API speaks) or null. */
  value: string | null;
  onChange: (iso: string | null) => void;
  placeholder?: string;
  minDate?: string | null;
  maxDate?: string | null;
  invalid?: boolean;
  disabled?: boolean;
  'aria-label'?: string;
}

const toDate = (iso: string | null | undefined): Date | undefined =>
  iso ? dayjs(iso, ISO_DATE, true).toDate() : undefined;

const EMPTY_MASK = '__-__-____';

/** Refuses a digit that can't make a real date. Runs in the capture phase, before the mask's
 *  own listener on the input takes the key. */
function refuseWrongDigit(e: KeyboardEvent<HTMLInputElement>, minYear?: number) {
  if (!/^\d$/.test(e.key) || e.ctrlKey || e.metaKey || e.altKey) return;
  const { value, selectionStart, selectionEnd } = e.currentTarget;
  const start = selectionStart ?? 0;
  const end = selectionEnd ?? start;
  // What's selected is about to go; the digit lands on the next place that isn't a dash.
  const shown = value || EMPTY_MASK;
  const typed = shown.slice(0, start) + EMPTY_MASK.slice(start, end) + shown.slice(end);
  const pos = typed[start] === '-' ? start + 1 : start;
  if (pos < EMPTY_MASK.length && !digitFitsDate(typed, pos, e.key, minYear)) {
    e.preventDefault();
    e.stopPropagation();
  }
}

/** Always shows MM-DD-YYYY (firm standard); always emits ISO. */
export function BaseDatePicker({
  id,
  value,
  onChange,
  placeholder = SHARED_COMPONENTS_TEXT.datePicker.placeholder,
  minDate,
  maxDate,
  invalid,
  disabled,
  'aria-label': ariaLabel,
}: BaseDatePickerProps) {
  return (
    <Calendar
      inputId={id}
      value={toDate(value) ?? null}
      onChange={(e) => {
        const d = e.value;
        onChange(d instanceof Date ? dayjs(d).format(ISO_DATE) : null);
      }}
      dateFormat="mm-dd-yy"
      // The dashes place themselves as you type.
      mask="99-99-9999"
      // Click or Tab goes straight to typing; the calendar opens from its icon.
      showOnFocus={false}
      placeholder={placeholder}
      minDate={toDate(minDate)}
      maxDate={toDate(maxDate)}
      invalid={invalid}
      disabled={disabled}
      showIcon
      showButtonBar
      // Name the text input itself (the one people type into), not the wrapper.
      pt={{
        input: {
          root: {
            ...(ariaLabel ? { 'aria-label': ariaLabel } : {}),
            onKeyDownCapture: (e: KeyboardEvent<HTMLInputElement>) => {
              refuseWrongDigit(e, minDate ? Number(minDate.slice(0, 4)) : undefined);
            },
          },
        },
      }}
      className="w-full"
      inputClassName="w-full"
    />
  );
}
